import { create } from 'zustand'

import { CLIENT_ID } from '@/api/client'
import {
  createFlashcard,
  deleteFlashcard,
  generateFlashcard,
  getFlashcards,
  getFlashcardStats,
  getReviewCards,
  reviewFlashcard,
} from '@/api/services/flashcard'
import { registerSyncListener } from '@/api/sync-events'
import type {
  FlashcardCreateRequest,
  FlashcardGenerateRequest,
  FlashcardGenerateResponse,
  FlashcardResponse,
  FlashcardReviewResponse,
  FlashcardStatsResponse,
} from '@/api/types/flashcard'

type FlashcardState = {
  cards: FlashcardResponse[]
  reviewCards: FlashcardResponse[]
  stats: FlashcardStatsResponse | null
  registeredSegments: Set<string>
  isLoading: boolean
  error: string | null
}

type FlashcardActions = {
  fetchCards: (episodeId?: number) => Promise<void>
  fetchReviewCards: () => Promise<void>
  fetchStats: () => Promise<void>
  addCard: (request: FlashcardCreateRequest) => Promise<FlashcardResponse>
  removeCard: (id: number) => Promise<void>
  submitReview: (id: number, rating: 1 | 2 | 3 | 4) => Promise<FlashcardReviewResponse>
  generate: (request: FlashcardGenerateRequest) => Promise<FlashcardGenerateResponse>
  isSegmentRegistered: (episodeId: number, segmentIndex: number) => boolean
}

/** episode_id:segment_index をキーにする */
const makeKey = (episodeId: number | null, segmentIndex: number) => `${episodeId}:${segmentIndex}`

export const useFlashcardStore = create<FlashcardState & FlashcardActions>((set, get) => ({
  cards: [],
  reviewCards: [],
  stats: null,
  registeredSegments: new Set(),
  isLoading: false,
  error: null,

  fetchCards: async (episodeId?: number) => {
    set({ isLoading: true, error: null })
    try {
      const cards = await getFlashcards(episodeId)
      const segments = new Set(cards.map((c) => makeKey(c.episode_id, c.segment_index)))
      set({ cards, registeredSegments: segments })
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to fetch flashcards' })
    } finally {
      set({ isLoading: false })
    }
  },

  fetchReviewCards: async () => {
    set({ isLoading: true, error: null })
    try {
      const reviewCards = await getReviewCards()
      set({ reviewCards })
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to fetch review cards' })
    } finally {
      set({ isLoading: false })
    }
  },

  fetchStats: async () => {
    try {
      const stats = await getFlashcardStats()
      set({ stats })
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to fetch stats' })
    }
  },

  addCard: async (request: FlashcardCreateRequest) => {
    const card = await createFlashcard(request)
    set((state) => {
      const newSegments = new Set(state.registeredSegments)
      newSegments.add(makeKey(card.episode_id, card.segment_index))
      return {
        cards: [card, ...state.cards],
        registeredSegments: newSegments,
      }
    })
    return card
  },

  removeCard: async (id: number) => {
    await deleteFlashcard(id)
    set((state) => {
      const removed = state.cards.find((c) => c.id === id)
      const newCards = state.cards.filter((c) => c.id !== id)
      const newSegments = new Set(state.registeredSegments)
      if (removed) {
        // 同じセグメントに他のカードがなければ削除
        const hasOther = newCards.some(
          (c) => c.episode_id === removed.episode_id && c.segment_index === removed.segment_index,
        )
        if (!hasOther) {
          newSegments.delete(makeKey(removed.episode_id, removed.segment_index))
        }
      }
      return { cards: newCards, registeredSegments: newSegments }
    })
  },

  submitReview: async (id: number, rating: 1 | 2 | 3 | 4) => {
    const result = await reviewFlashcard(id, { rating })
    return result
  },

  generate: async (request: FlashcardGenerateRequest) => {
    return await generateFlashcard(request)
  },

  isSegmentRegistered: (episodeId: number, segmentIndex: number) => {
    return get().registeredSegments.has(makeKey(episodeId, segmentIndex))
  },
}))

// ============================================================================
// SSE リモートイベント購読
// ============================================================================
// 他端末でフラッシュカードが作成/更新/削除されたら、既にロード済みのデータを
// 再取得する（cards / reviewCards / stats）。
registerSyncListener((event) => {
  if (event.type !== 'flashcard.created' && event.type !== 'flashcard.updated' && event.type !== 'flashcard.deleted')
    return
  if (event.origin === CLIENT_ID) return

  const state = useFlashcardStore.getState()
  // ロード済みの領域だけ再取得（未使用ページの無駄なリクエストを避ける）
  if (state.cards.length > 0 || event.type === 'flashcard.created') {
    void state.fetchCards().catch(() => {})
  }
  if (state.reviewCards.length > 0) {
    void state.fetchReviewCards().catch(() => {})
  }
  if (state.stats !== null) {
    void state.fetchStats().catch(() => {})
  }
})
